import React, { useEffect, useState } from "react";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import { SweetAlertShow, LoadingDiv } from "../master/Master";
import usePrivateLink from "../config/usePrivateLink";
import {
  studentDueFeeDetailsV4,
  studentFeeDepositV4,
  studentFeeReceiptNo,
} from "../../api/Student";
import { useLocation, useNavigate } from "react-router-dom";
import { BankList } from "../../api/BankService";
import { feeTypes } from "../config/Data";

// Splits a receipt number like "RCPT-1511"
const splitReceiptNo = (value) => {
  const match = String(value ?? "").match(/^(\D*)(\d+)$/);

  if (match) {
    return { prefix: match[1], number: match[2] };
  }

  return { prefix: "", number: value ?? "" };
};

export default function StudentFeeDepositV4() {
  usePrivateLink();

  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const [filters, setFilters] = useState({});
  const [fromFilters, setFromFilters] = useState({ batch_id: "" });
  const [setting, setSetting] = useState({});

  useEffect(() => {
    setSetting(JSON.parse(localStorage.getItem("setting") ?? {}));

    setFilters({
      id: searchParams.get("id") ?? false,
      type: searchParams.get("type") ?? false,
      student_id: searchParams.get("student_id") ?? false,
      library_student_id: searchParams.get("library_student_id") ?? false,
      hostel_student_id: searchParams.get("hostel_student_id") ?? false,
    });
  }, []);

  const [data, setData] = useState({});
  const [receiptNoData, setReceiptNoData] = useState({});
  const [banks, setBanks] = useState([]);

  const [formData, setFormData] = useState({
    receipt_no: "RCPT-1",
    amount: "",
    bank_id: "",
    transaction_no: "",
    remark: "",
    payment_date: "",
    next_payment_date: "",
    feeData: [],
  });

  const [receiptParts, setReceiptParts] = useState(splitReceiptNo("RCPT-1"));

  useEffect(() => {
    receiptNoVerify();
  }, [formData?.receipt_no]);

  const receiptNoVerify = async () => {
    try {
      const response = await studentFeeReceiptNo(
        `receipt_no=${formData?.receipt_no}`,
      );

      if (response.data.status) {
        setReceiptNoData(response?.data?.data);

        if (formData.receipt_no === "RCPT-1") {
          const nextReceipt = response?.data?.data?.next_receipt_no;

          setFormData((prev) => ({
            ...prev,
            receipt_no: nextReceipt,
          }));

          setReceiptParts(splitReceiptNo(nextReceipt));
        }
      } else {
        SweetAlertShow("info", "Warning", response.data.message);
      }
    } catch (error) {
      SweetAlertShow("error", "Error", error.message);
    }
  };

  const handleReceiptNumberChange = (value) => {
    const digitsOnly = value.replace(/\D/g, "");

    setReceiptParts((prev) => {
      const updated = {
        ...prev,
        number: digitsOnly,
      };

      setFormData((fd) => ({
        ...fd,
        receipt_no: `${updated.prefix}${updated.number}`,
      }));

      return updated;
    });
  };

  const getDataList = async () => {
    if (
      !filters?.id &&
      !filters?.student_id &&
      !filters?.library_student_id &&
      !filters?.hostel_student_id
    ) {
      return null;
    }

    setLoading(true);

    try {
      let addUrl = "";

      Object.keys(filters).forEach((key) => {
        const value = filters[key];

        if (value) {
          addUrl += `&${key}=${encodeURIComponent(value)}`;
        }
      });

      const response = await studentDueFeeDetailsV4(addUrl);

      if (response.data.status) {
        setData(response.data.data);
      } else {
        SweetAlertShow("info", "Warning", response.data.message);
      }
    } catch (error) {
      SweetAlertShow("error", "Error", error.message);
    } finally {
      setLoading(false);
    }
  };

  const getBankList = async () => {
    setLoading(true);

    try {
      const response = await BankList();

      if (response.data.status) {
        setBanks(response.data.data);
      } else {
        SweetAlertShow("info", "Warning", response.data.message);
      }
    } catch (error) {
      SweetAlertShow("error", "Error", error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getBankList();

    const today = new Date();
    const next = new Date();

    next.setMonth(next.getMonth() + 1);

    setFormData((prev) => ({
      ...prev,
      payment_date: today.toLocaleDateString("en-Us"),
      next_payment_date: next.toLocaleDateString("en-Us"),
    }));
  }, []);

  useEffect(() => {
    getDataList();
  }, [filters]);

  useEffect(() => {
    const batchFees =
      data?.batch_n_fees?.filter((item) => {
        if (fromFilters?.batch_id) {
          return Number(item.batch_id) === Number(fromFilters.batch_id);
        }

        return true;
      }) || [];

    const library_fees = data?.library_student_fees || [];
    const hostel_fees = data?.hostel_student_fees || [];
    const allBusFees = data?.bus_student_fees || [];

    // When a batch is selected, show transport fees only for that batch's session.
    // With "All" selected, keep every session's transport fee as a separate row.
    const selectedBatch = fromFilters?.batch_id
      ? data?.batch_n_fees?.find(
        (item) => Number(item.batch_id) === Number(fromFilters.batch_id),
      )
      : null;

    const selectedSessionId = selectedBatch?.session_id;

    const bus_fees = selectedSessionId
      ? allBusFees.filter(
        (item) => Number(item.session_id) === Number(selectedSessionId),
      )
      : allBusFees;

    const libraryIds = library_fees.map((item) => item.id);
    const hostelIds = hostel_fees.map((item) => item.id);

    const sumTotals = (arr) =>
      arr.reduce(
        (acc, item) => {
          acc.total += Math.ceil(Number(item.final_amount || 0));
          acc.paid += Math.floor(Number(item.paid_amount || 0));

          return acc;
        },
        { total: 0, paid: 0 },
      );

    const library_fees_totals = sumTotals(library_fees);
    const hostel_fees_totals = sumTotals(hostel_fees);

    const bus_fees_totals = bus_fees.reduce(
      (acc, item) => {
        acc.total += Math.ceil(Number(item.final_amount || 0));
        acc.paid += Math.floor(Number(item.paid_amount || 0));
        acc.pending += Math.max(
          0,
          Math.ceil(
            Number(
              item.due_amount ??
              (Number(item.final_amount || 0) - Number(item.paid_amount || 0)),
            ),
          ),
        );

        return acc;
      },
      { total: 0, paid: 0, pending: 0 },
    );

    const bus_fees_pending = bus_fees_totals.pending;

    const library_fees_pending =
      library_fees_totals.total - library_fees_totals.paid;

    const hostel_fees_pending =
      hostel_fees_totals.total - hostel_fees_totals.paid;

    const feeMap = {};

    batchFees.forEach((batch) => {
      batch.student_fee_details.forEach((fee) => {
        if (!feeMap[fee.fee_type]) {
          feeMap[fee.fee_type] = {
            name: fee.fee_type,
            ids: [],
            batch_id: [],
            total: 0,
            paid: 0,
            pending: 0,
          };
        }

        feeMap[fee.fee_type].ids.push(batch.id);
        feeMap[fee.fee_type].batch_id.push(batch.batch_id);

        feeMap[fee.fee_type].total += Number(fee.final_amount || 0);

        feeMap[fee.fee_type].paid += Number(fee.paid_amount || 0);

        feeMap[fee.fee_type].pending += Number(fee.due_amount || 0);
      });
    });

    const mergeModuleFee = (name, ids, total, paid, pending) => {
      if (!feeMap[name]) {
        feeMap[name] = {
          name,
          ids: [],
          batch_id: [],
          total: 0,
          paid: 0,
          pending: 0,
        };
      }

      feeMap[name].ids = [...feeMap[name].ids, ...ids];

      feeMap[name].total += total;
      feeMap[name].paid += paid;
      feeMap[name].pending += pending;
    };

    mergeModuleFee(
      "Hostel Fee",
      hostelIds,
      hostel_fees_totals.total,
      hostel_fees_totals.paid,
      hostel_fees_pending,
    );

    mergeModuleFee(
      "Library Fee",
      libraryIds,
      library_fees_totals.total,
      library_fees_totals.paid,
      library_fees_pending,
    );

    const regularFeeData = Object.values(feeMap).map((item) => ({
      ...item,
      key: `fee-${item.name}`,
      label: item.name,
      selected: item.pending > 0,
      payAmount: item.pending,
    }));

    // Batch selected: show that batch/session transport fee only.
    // "All" selected: merge every transport record into one Transport Fee row.
    // Keep all underlying bus fee IDs so the payment handler can allocate the
    // payment across the original records.
    const transportFeeData = (() => {
      if (!bus_fees.length) return [];

      const makeTransportFee = (items, isAll = false) => {
        const total = items.reduce(
          (sum, item) => sum + Math.ceil(Number(item.final_amount || 0)),
          0,
        );
        const paid = items.reduce(
          (sum, item) => sum + Math.floor(Number(item.paid_amount || 0)),
          0,
        );
        const pending = items.reduce(
          (sum, item) =>
            sum + Math.max(
              0,
              Math.ceil(
                Number(
                  item.due_amount ??
                  (Number(item.final_amount || 0) - Number(item.paid_amount || 0)),
                ),
              ),
            ),
          0,
        );

        const firstItem = items[0];
        const paymentType = isAll
          ? "Transport"
          : firstItem.type || firstItem.payment_type || firstItem?.bus_student?.payment_type || "Transport";

        return {
          name: "Transport Fee",
          label: "Transport Fee",
          key: isAll ? "transport-all" : `transport-${firstItem.id}`,
          ids: items.map((item) => item.id),
          batch_id: [],
          transport_fee_id: isAll ? null : firstItem.id,
          transport_fee_ids: items.map((item) => item.id),
          bus_student_id: isAll ? null : firstItem.bus_student_id,
          session_id: isAll ? null : firstItem.session_id,
          payment_type: paymentType,
          total,
          paid,
          pending,
          selected: pending > 0,
          payAmount: pending,
        };
      };

      // When a specific batch is selected, retain the batch/session-specific row.
      if (selectedSessionId) {
        return bus_fees.map((item) => makeTransportFee([item], false));
      }

      // All batches: show one combined Transport Fee row with summed totals.
      return [makeTransportFee(bus_fees, true)];
    })();

    const feeData = [...regularFeeData, ...transportFeeData];

    const totalAmount = feeData.reduce(
      (sum, item) =>
        item.selected ? sum + Math.ceil(Number(item.payAmount || 0)) : sum,
      0,
    );

    setFormData((prev) => ({
      ...prev,
      feeData,
      amount: totalAmount,
    }));
  }, [data, fromFilters]);

  const handleSubmit = async (e) => {
    setLoading(true);

    e.preventDefault();

    try {
      const formDataObj = new FormData(e.target);

      const response = await studentFeeDepositV4(formDataObj);

      if (response.data.status) {
        navigate(`/student/fees/receipt?id=${response?.data?.data?.id}`);

        SweetAlertShow("success", "Successfully", response.data.message);
      } else {
        SweetAlertShow("info", "Warning", response.data.message);
      }
    } catch (error) {
      SweetAlertShow("error", "Error", error.response.data.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckbox = (index) => {
    const updated = [...formData.feeData];

    const clickedFee = updated[index];

    clickedFee.selected = !clickedFee.selected;

    if (clickedFee.name === "Tuition Fee" && clickedFee.selected) {
      const regIndex = updated.findIndex((f) => f.name === "Registration Fee");

      if (regIndex !== -1) {
        updated[regIndex].selected = true;
        updated[regIndex].payAmount = updated[regIndex].pending;
      }
    }

    if (clickedFee.name === "Registration Fee" && !clickedFee.selected) {
      const tuition = updated.find((f) => f.name === "Tuition Fee");

      if (tuition?.selected) {
        return;
      }
    }

    if (!clickedFee.selected) {
      clickedFee.payAmount = 0;
    }

    const totalSelectedAmount = updated.reduce(
      (sum, fee) =>
        fee.selected ? sum + Math.ceil(Number(fee.payAmount || 0)) : sum,
      0,
    );

    setFormData((prev) => ({
      ...prev,
      feeData: updated,
      amount: totalSelectedAmount,
    }));
  };

  const handleAmountChange = (index, value) => {
    const updated = [...formData.feeData];

    let amount = Math.ceil(Number(value || 0));

    if (updated[index].name !== "Hostel Fee") {
      if (amount > updated[index].pending) {
        amount = updated[index].pending;
      }
    }

    updated[index].payAmount = amount;

    const total = updated.reduce(
      (sum, fee) =>
        fee.selected ? sum + Math.ceil(Number(fee.payAmount || 0)) : sum,
      0,
    );

    setFormData((prev) => ({
      ...prev,
      feeData: updated,
      amount: total,
    }));
  };

  const handleTotalAmountChange = (value) => {
    let amount = Math.ceil(Number(value || 0));

    const hostelFee = formData.feeData.find((f) => f.name === "Hostel Fee");

    const isHostelSelected = hostelFee?.selected;

    const nonHostelPending = formData?.feeData
      ?.filter((f) => f.name !== "Hostel Fee")
      .reduce((sum, item) => sum + Math.ceil(Number(item.pending || 0)), 0);

    if (!isHostelSelected && amount > nonHostelPending) {
      amount = nonHostelPending;
    }

    let remaining = amount;

    const fixedPriority = [
      "Previous Session Fees",
      ...feeTypes,
      "Library Fee",
      "Transport Fee",
      "Hostel Fee",
    ];

    const updated = [...formData.feeData]
      .sort((a, b) => {
        const ai = fixedPriority.indexOf(a.name);
        const bi = fixedPriority.indexOf(b.name);

        if (ai === -1 && bi === -1) return 0;
        if (ai === -1) return 1;
        if (bi === -1) return -1;

        return ai - bi;
      })
      .map((fee) => {
        if (!fee.selected) {
          return {
            ...fee,
            payAmount: 0,
          };
        }

        let pay = 0;

        if (remaining > 0) {
          if (fee.name === "Hostel Fee" && isHostelSelected) {
            pay = remaining;
          } else {
            pay = Math.min(fee.pending, remaining);
          }

          remaining -= pay;
        }

        return {
          ...fee,
          payAmount: pay,
        };
      });

    setFormData((prev) => ({
      ...prev,
      amount,
      feeData: updated,
    }));
  };

  /* ----------------------------------------------------------
     UI HELPERS ONLY
     ---------------------------------------------------------- */

  const totalFee = formData?.feeData?.reduce(
    (total, item) => total + Math.ceil(Number(item.total || 0)),
    0,
  );

  const totalPaid = formData?.feeData?.reduce(
    (paid, item) => paid + Math.ceil(Number(item.paid || 0)),
    0,
  );

  const totalPending = formData?.feeData?.reduce(
    (pending, item) => pending + Math.ceil(Number(item.pending || 0)),
    0,
  );

  const selectedFeeCount =
    formData?.feeData?.filter((fee) => fee.selected).length || 0;

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="container-fluid flex-grow-1 container-p-y">
              {/* =====================================================
                  STUDENT HEADER
              ====================================================== */}
              <div className="card border-0 shadow-sm mb-3">
                <div className="card-body py-3">
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                    <div className="d-flex align-items-center gap-3">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center bg-label-primary"
                        style={{
                          width: 48,
                          height: 48,
                          flexShrink: 0,
                        }}
                      >
                        <i className="ri-user-3-line fs-4 text-primary" />
                      </div>

                      <div>
                        <div className="fw-bold fs-5">
                          {data?.name || "Student"}
                        </div>

                        <div className="text-muted small">
                          Registration No:{" "}
                          <span className="fw-semibold text-body">
                            {data?.registration_no || "-"}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="d-flex gap-2 flex-wrap">
                      <div className="px-3 py-2 rounded bg-label-primary">
                        <div className="text-muted small">Total Fee</div>
                        <div className="fw-bold">₹{totalFee || 0}</div>
                      </div>

                      <div className="px-3 py-2 rounded bg-label-success">
                        <div className="text-muted small">Paid</div>
                        <div className="fw-bold text-success">
                          ₹{totalPaid || 0}
                        </div>
                      </div>

                      <div className="px-3 py-2 rounded bg-label-danger">
                        <div className="text-muted small">Pending</div>
                        <div className="fw-bold text-danger">
                          ₹{totalPending || 0}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row g-3">
                {/* =====================================================
                    LEFT - FEE SUMMARY
                ====================================================== */}
                <div className="col-xl-5 col-lg-5">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-header border-bottom py-3">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <h5 className="mb-1">Fee Summary</h5>
                          <small className="text-muted">
                            Current fee status
                          </small>
                        </div>

                        <span className="badge bg-label-primary rounded-pill">
                          {formData?.feeData?.filter((f) => f.total > 0)
                            .length || 0}{" "}
                          Types
                        </span>
                      </div>
                    </div>

                    <div className="card-body p-3">
                      <div className="row g-2">
                        {formData?.feeData?.map((f) => {
                          if (f.total === 0) return null;

                          return (
                            <div className="col-12" key={f.key || f.name}>
                              <div
                                className="border rounded-3 p-3"
                                style={{
                                  background: "var(--bs-card-bg)",
                                }}
                              >
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <div className="fw-semibold">{f.label || f.name}</div>

                                  {f.pending > 0 ? (
                                    <span className="badge bg-label-danger">
                                      Pending
                                    </span>
                                  ) : (
                                    <span className="badge bg-label-success">
                                      Paid
                                    </span>
                                  )}
                                </div>

                                <div className="row g-2 small">
                                  <div className="col-4">
                                    <div className="text-muted">Total</div>
                                    <div className="fw-semibold">
                                      ₹{f.total}
                                    </div>
                                  </div>

                                  <div className="col-4">
                                    <div className="text-muted">Paid</div>
                                    <div className="fw-semibold text-success">
                                      ₹{f.paid}
                                    </div>
                                  </div>

                                  <div className="col-4">
                                    <div className="text-muted">Due</div>
                                    <div className="fw-semibold text-danger">
                                      ₹{f.pending}
                                    </div>
                                  </div>
                                </div>

                                {f.total > 0 && (
                                  <div
                                    className="progress mt-3"
                                    style={{
                                      height: 5,
                                    }}
                                  >
                                    <div
                                      className="progress-bar bg-success"
                                      style={{
                                        width: `${Math.min(
                                          100,
                                          (f.paid / f.total) * 100,
                                        )}%`,
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="card-footer border-top">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="fw-semibold">Total Outstanding</span>

                        <span className="fs-5 fw-bold text-danger">
                          ₹{totalPending || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* =====================================================
                    RIGHT - PAYMENT
                ====================================================== */}
                <div className="col-xl-7 col-lg-7">
                  <form onSubmit={handleSubmit}>
                    <div className="card border-0 shadow-sm">
                      {/* PAYMENT HEADER */}
                      <div className="card-header bg-label-primary border-0 py-3">
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                          <div className="d-flex align-items-center gap-2">
                            <div
                              className="rounded-circle bg-primary d-flex align-items-center justify-content-center"
                              style={{
                                width: 38,
                                height: 38,
                              }}
                            >
                              <i className="ri-bank-card-line text-white" />
                            </div>

                            <div>
                              <h5 className="mb-0 text-primary">
                                Deposit Payment
                              </h5>

                              <small className="text-muted">
                                Enter payment details
                              </small>
                            </div>
                          </div>

                          {/* RECEIPT */}
                          <div style={{ minWidth: 220 }}>
                            <label
                              htmlFor="receipt_no_number"
                              className="form-label mb-1 small fw-semibold text-primary"
                            >
                              Receipt No <sup className="text-danger">*</sup>
                            </label>

                            <div className="input-group">
                              {receiptParts.prefix && (
                                <span
                                  className="input-group-text fw-semibold"
                                  style={{
                                    background:
                                      "rgba(var(--bs-primary-rgb), .08)",
                                    color: "var(--bs-primary)",
                                  }}
                                >
                                  {receiptParts.prefix}
                                </span>
                              )}

                              <input
                                type="text"
                                inputMode="numeric"
                                id="receipt_no_number"
                                className="form-control fw-semibold"
                                placeholder="Receipt No"
                                value={receiptParts.number}
                                onChange={(e) =>
                                  handleReceiptNumberChange(e.target.value)
                                }
                                required
                              />
                            </div>

                            <input
                              type="hidden"
                              name="receipt_no"
                              value={formData?.receipt_no}
                            />

                            {receiptNoData?.message && (
                              <span className="text-danger small">
                                {receiptNoData.message}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="card-body p-3 p-md-4">
                        {/* =================================================
                            PAYMENT INFORMATION
                        ================================================== */}
                        <div className="mb-4">
                          <div className="d-flex align-items-center gap-2 mb-3">
                            <span className="badge bg-label-primary p-2">
                              <i className="ri-money-rupee-circle-line" />
                            </span>

                            <div>
                              <h6 className="mb-0">Payment Information</h6>
                              <small className="text-muted">
                                Amount and transaction details
                              </small>
                            </div>
                          </div>

                          <div className="row g-3">
                            <input
                              type="hidden"
                              name="student_id"
                              value={data?.student_id}
                            />

                            <input
                              type="hidden"
                              name="hostel_student_id"
                              value={data?.hostel_student_id}
                            />

                            <input
                              type="hidden"
                              name="library_student_id"
                              value={data?.library_student_id}
                            />

                            <input
                              type="hidden"
                              name="bus_student_id"
                              value={data?.bus_student_id}
                            />

                            {data?.batch_n_fees?.length > 1 && (
                              <div className="col-md-6">
                                <div className="form-floating form-floating-outline">
                                  <select
                                    className="form-select"
                                    onChange={(e) =>
                                      setFromFilters({
                                        ...fromFilters,
                                        batch_id: e.target.value,
                                      })
                                    }
                                  >
                                    <option value="">All</option>

                                    {data?.batch_n_fees?.map((item) => (
                                      <option
                                        key={item.id}
                                        value={item.batch.id}
                                      >
                                        {item.batch.title}
                                      </option>
                                    ))}
                                  </select>

                                  <label>Select Batch</label>
                                </div>
                              </div>
                            )}

                            <div
                              className={
                                data?.batch_n_fees?.length > 1
                                  ? "col-md-6"
                                  : "col-md-12"
                              }
                            >
                              <div className="form-floating form-floating-outline">
                                <input
                                  type="number"
                                  name="amount"
                                  id="amount"
                                  className="form-control fw-semibold"
                                  placeholder="Enter Amount"
                                  min={0}
                                  value={formData?.amount}
                                  onChange={(e) =>
                                    handleTotalAmountChange(e.target.value)
                                  }
                                  required
                                />

                                <label htmlFor="amount">
                                  Amount <sup className="text-danger">*</sup>
                                </label>
                              </div>
                            </div>

                            <div className="col-md-6">
                              <div className="form-floating form-floating-outline">
                                <select
                                  name="bank_id"
                                  id="bank_id"
                                  className="form-select"
                                  required
                                  value={formData?.bank_id ?? ""}
                                  onChange={(e) =>
                                    setFormData({
                                      ...formData,
                                      bank_id: e.target.value,
                                    })
                                  }
                                >
                                  <option disabled value="">
                                    Select Bank
                                  </option>

                                  {banks?.map((item) => (
                                    <option key={item?.id} value={item?.id}>
                                      {item?.account_no}:(
                                      {item?.owner_name})
                                    </option>
                                  ))}
                                </select>

                                <label htmlFor="bank_id">
                                  Select Bank{" "}
                                  <sup className="text-danger">*</sup>
                                </label>
                              </div>
                            </div>

                            <div className="col-md-6">
                              <div className="form-floating form-floating-outline">
                                <input
                                  type="text"
                                  name="transaction_no"
                                  id="transaction_no"
                                  className="form-control"
                                  placeholder="Enter Transaction No"
                                  value={formData?.transaction_no}
                                  onChange={(e) =>
                                    setFormData({
                                      ...formData,
                                      transaction_no: e.target.value,
                                    })
                                  }
                                />

                                <label htmlFor="transaction_no">
                                  Transaction No
                                </label>
                              </div>
                            </div>

                            <div className="col-md-6">
                              <div className="form-floating form-floating-outline">
                                <input
                                  type="date"
                                  name="payment_date"
                                  id="payment_date"
                                  className="form-control"
                                  value={formData?.payment_date}
                                  onChange={(e) =>
                                    setFormData({
                                      ...formData,
                                      payment_date: e.target.value,
                                    })
                                  }
                                />

                                <label htmlFor="payment_date">
                                  Payment Date
                                </label>
                              </div>
                            </div>

                            <div className="col-md-6">
                              <div className="form-floating form-floating-outline">
                                <input
                                  type="date"
                                  name="next_payment_date"
                                  id="next_payment_date"
                                  className="form-control"
                                  value={formData?.next_payment_date}
                                  onChange={(e) =>
                                    setFormData({
                                      ...formData,
                                      next_payment_date: e.target.value,
                                    })
                                  }
                                />

                                <label htmlFor="next_payment_date">
                                  Next Payment Date
                                </label>
                              </div>
                            </div>

                            <div className="col-12">
                              <div className="form-floating form-floating-outline">
                                <textarea
                                  name="remark"
                                  id="remark"
                                  className="form-control input-uppercase"
                                  placeholder="Enter Remark"
                                  style={{
                                    minHeight: 80,
                                  }}
                                  value={formData?.remark}
                                  onChange={(e) =>
                                    setFormData({
                                      ...formData,
                                      remark: e.target.value,
                                    })
                                  }
                                />

                                <label htmlFor="remark">Remark</label>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* =================================================
                            FEE ALLOCATION
                        ================================================== */}
                        <div className="mb-4">
                          <div className="d-flex align-items-center justify-content-between mb-3">
                            <div className="d-flex align-items-center gap-2">
                              <span className="badge bg-label-warning p-2">
                                <i className="ri-list-check-2" />
                              </span>

                              <div>
                                <h6 className="mb-0">Fee Allocation</h6>

                                <small className="text-muted">
                                  Select fees and adjust payment
                                </small>
                              </div>
                            </div>

                            <span className="badge bg-label-primary">
                              {selectedFeeCount} Selected
                            </span>
                          </div>

                          <div className="d-flex flex-column gap-2">
                            {(() => {
                              let i = -1;

                              return formData?.feeData?.map((fee, index) => {
                                if (fee.pending === 0) {
                                  return null;
                                }

                                if (fee.selected) {
                                  i++;
                                }

                                return (
                                  <div
                                    key={fee.key || `${fee.name}-${index}`}
                                    className={`border rounded-3 p-3 ${fee.selected ? "border-primary" : ""
                                      }`}
                                    style={{
                                      transition: "all .2s ease",
                                      background: fee.selected
                                        ? "rgba(var(--bs-primary-rgb), .04)"
                                        : "transparent",
                                    }}
                                  >
                                    <div className="row align-items-center g-2">
                                      <div className="col-12 col-md-6">
                                        <div className="d-flex align-items-center gap-3">
                                          <div className="form-check mb-0">
                                            <input
                                              className="form-check-input"
                                              type="checkbox"
                                              name={`module[${i}][type]`}
                                              value={fee.name}
                                              id={`fee-${index}`}
                                              checked={fee.selected}
                                              onChange={() =>
                                                handleCheckbox(index)
                                              }
                                              style={{
                                                width: 18,
                                                height: 18,
                                              }}
                                            />

                                            <label
                                              className="form-check-label fw-semibold"
                                              htmlFor={`fee-${index}`}
                                            >
                                              {fee.label || fee.name}
                                            </label>
                                          </div>
                                        </div>

                                        <div className="mt-1 ms-4 small text-muted">
                                          Pending:{" "}
                                          <span className="text-danger fw-semibold">
                                            ₹{fee.pending}
                                          </span>
                                        </div>

                                        <input
                                          type="hidden"
                                          name={`module[${i}][ids]`}
                                          value={fee.ids}
                                          disabled={!fee.selected}
                                        />
                                      </div>

                                      <div className="col-12 col-md-6">
                                        <div className="input-group">
                                          <span className="input-group-text">
                                            ₹
                                          </span>

                                          <input
                                            type="number"
                                            className="form-control fw-semibold"
                                            value={fee.payAmount}
                                            min={0}
                                            name={`module[${i}][amount]`}
                                            disabled={!fee.selected}
                                            max={
                                              fee.name === "Hostel Fee"
                                                ? undefined
                                                : fee.pending
                                            }
                                            onChange={(e) => {
                                              if (
                                                fee.name !== "Registration Fee"
                                              ) {
                                                handleAmountChange(
                                                  index,
                                                  e.target.value,
                                                );
                                              }
                                            }}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        </div>

                        {/* =================================================
                            FOOTER ACTION
                        ================================================== */}
                        <div className="border-top pt-3">
                          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                            {setting?.message_status === "Yes" ? (
                              <div className="form-check form-switch">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="sendMessage"
                                  name="sendMessage"
                                  defaultChecked={true}
                                  value={1}
                                />

                                <label
                                  className="form-check-label"
                                  htmlFor="sendMessage"
                                >
                                  Send payment confirmation
                                </label>
                              </div>
                            ) : (
                              <div />
                            )}

                            <div className="d-flex align-items-center gap-3">
                              <div className="text-end">
                                <div className="small text-muted">Paying</div>

                                <div className="fs-4 fw-bold text-primary">
                                  ₹{Math.ceil(Number(formData?.amount || 0))}
                                </div>
                              </div>

                              <button
                                type="submit"
                                className="btn btn-primary px-4 py-2"
                              >
                                <i className="ri-check-line me-1" />
                                Save Payment
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
